import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, MouseEvent, useMemo, useRef, useState } from 'react';
import zeplinColor from '../../../../../../theme/Color';
import { CalendarEvent, getVisibleEvents } from '../../../../../../utilities/eventUtilies';
import CalendarCard from '../../../CalendarMain/CalendarCard/CalendarCard';
import { ProvisionalEvent } from '../../CalendarGrid';
import { MoreEventsModal } from './MoreEventsModal';

interface GridContentCellProps {
  colIndex: number;
  dateColumns: {
    date: dayjs.Dayjs;
    dayName: string;
    dayNumber: string;
    fullFormat: string;
    isToday: boolean;
    isSelected: boolean;
  }[];
  dateCol: {
    date: Dayjs;
  };
  timeSlot: {
    hour24: string;
    hourIndex: number;
  };
  slotEvents: CalendarEvent[];
  isCurrentDate: boolean;
  isCurrentHour: boolean;
  hourEvents: Record<string, number>;
  handleClick: (event: MouseEvent<HTMLDivElement>) => void;
  setSelectedEvent: Dispatch<React.SetStateAction<CalendarEvent | undefined>>;
  handleCellClick: (
    date: Dayjs,
    timeSlot: { hourIndex: number; hour24: string },
    targetElement: HTMLDivElement,
  ) => void;
  provisionalEvent: ProvisionalEvent | null;
  isEditingExisting: boolean;
}

export const GridContentCell = ({
  colIndex,
  dateColumns,
  dateCol,
  timeSlot,
  slotEvents,
  isCurrentDate,
  isCurrentHour,
  hourEvents,
  handleClick,
  setSelectedEvent,
  handleCellClick,
  provisionalEvent,
  isEditingExisting,
}: GridContentCellProps) => {
  const dateColumnWidth = 184;
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.up('xs'));
  const rowHeight = isXS ? 70 : 115;
  const cellRef = useRef<HTMLDivElement>(null);

  const shouldShowProvisional =
    !isEditingExisting &&
    provisionalEvent &&
    provisionalEvent.columnKey === dateCol.date.format('YYYY-MM-DD') &&
    provisionalEvent.start.hour() === timeSlot.hourIndex;

  const { visibleEvents, remainingCount } = useMemo(
    () => getVisibleEvents(slotEvents),
    [slotEvents],
  );

  const maxCardHeight = useMemo(() => {
    if (visibleEvents.length === 0) return 0;

    const heights = visibleEvents.map((event) => {
      const start = dayjs(event.start);
      const end = dayjs(event.end);
      const duration = end.diff(start, 'minute') / 60;
      return duration * rowHeight;
    });

    return Math.max(...heights);
  }, [visibleEvents, rowHeight]);

  const hasMore = remainingCount > 0;

  const [isMoreModalOpen, setIsMoreModalOpen] = useState(false);
  const morePillRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      sx={{
        minWidth: `${dateColumnWidth}px`,
        paddingTop: '16px',
        flex: 1,
        borderRight:
          colIndex === dateColumns.length - 1 ? 'none' : `1px solid ${zeplinColor.Neutral5}`,
      }}
      ref={cellRef}
      onClick={(e) => {
        handleCellClick(dateCol.date, timeSlot, e.currentTarget);
      }}
    >
      <Box
        key={`${dateCol.date.format('YYYY-MM-DD')}-${timeSlot.hour24}`}
        sx={{
          borderTop: `1px solid ${zeplinColor.Neutral5}`,
          paddingTop: '16px',
          backgroundColor: zeplinColor.Neutral1,
          position: 'relative',
          cursor: 'pointer',
          gap: '2px',
        }}
        onClick={() => {}}
      >
        {isCurrentDate && isCurrentHour && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: dayjs().minute() * (rowHeight / 60) + 'px',
              zIndex: 100,
            }}
          >
            <img src={'/assets/time-indicator.svg'} alt={'time-indicator'} width={'100%'} />
          </Box>
        )}
        {shouldShowProvisional && (
          <Box
            sx={{
              position: 'absolute',
              top: `${provisionalEvent.start.minute() * (rowHeight / 60) + 1}px`,
              height: `${provisionalEvent.duration * (rowHeight / 60) - 2}px`,
              left: '4px',
              right: '4px',
              backgroundColor: zeplinColor.Neutral1,
              borderRadius: '14px',
              border: `1.2px solid ${zeplinColor.BasicBlack}`,
              zIndex: 10,
              display: 'flex',
              padding: '12px',
            }}
          >
            <Typography
              variant='label1'
              sx={{
                color: zeplinColor.Neutral8,
              }}
            >
              {`${provisionalEvent.start.format('h:mma')} - ${provisionalEvent.end.format(
                'h:mma',
              )}`}
            </Typography>
          </Box>
        )}

        {visibleEvents.map((event, eventIndex) => {
          const isFirst = eventIndex === 0;
          const start = dayjs(event.start);
          const end = dayjs(event.end);
          const calculatedHeight = (end.diff(start, 'minute') / 60) * rowHeight;
          const topOffset = start.minute() * (rowHeight / 60);

          const availableWidth = hasMore ? 'calc(100% - 38px)' : 'calc(100% - 4px)';
          const left = isFirst ? '2px' : '32px';
          const width = isFirst ? availableWidth : `calc(${availableWidth} - 28px)`;

          return (
            <Box
              key={event.id}
              sx={{
                position: 'absolute',
                top: `${topOffset}px`,
                left: left,
                width: width,
                borderRadius: '16px',
                height: `${calculatedHeight - 2}px`,
                zIndex: eventIndex + 1,
                transition: 'all 0.2s ease-in-out',
                boxShadow: !isFirst ? '-4px 0px 10px rgba(0,0,0,0.1)' : 'none',
                // '&:hover': { zIndex: 10 },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleClick(e);
                setSelectedEvent(event);
              }}
            >
              <CalendarCard height={calculatedHeight - 2} event={event} />
            </Box>
          );
        })}

        {hasMore && (
          <Box
            sx={{
              position: 'absolute',
              top: `${dayjs(visibleEvents[0].start).minute() * (rowHeight / 60)}px`,
              right: '2px',
              width: '30px',
              height: `${maxCardHeight - 2}px`,
              backgroundColor: zeplinColor.Neutral1,
              border: `1.5px solid ${zeplinColor.Neutral5}`,
              borderRadius: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              cursor: 'pointer',
              transition: 'height 0.2s ease',
              '&:hover': {
                backgroundColor: zeplinColor.Neutral2,
              },
            }}
            ref={morePillRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsMoreModalOpen(true);
            }}
          >
            <Typography
              variant='buttonSmall'
              sx={{
                color: zeplinColor.BasicBlack,
              }}
            >
              +{remainingCount}
            </Typography>
          </Box>
        )}
        <MoreEventsModal
          open={isMoreModalOpen}
          onClose={() => {
            setIsMoreModalOpen(false);
          }}
          events={slotEvents}
          date={dateCol.date}
          hour={timeSlot.hour24}
          onSelectEvent={(e: MouseEvent<HTMLDivElement>, event: any) => {
            setSelectedEvent(event);

            if (morePillRef.current) {
              const syntheticEvent = {
                currentTarget: morePillRef.current,
                stopPropagation: () => {},
                preventDefault: () => {},
              } as unknown as MouseEvent<HTMLDivElement>;

              handleClick(syntheticEvent);
            }

            setIsMoreModalOpen(false);
          }}
        />
      </Box>
    </Box>
  );
};
